"use client";

import { useState } from "react";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Link } from "@/components/common/Link";
import { Play } from "lucide-react";
function ProjectsGridConsumer() {
  const {
    posts,
    loading,
    error
  } = usePosts();
  const [activeCategory, setActiveCategory] = useState("All Work");
  const categories = ["All Work", "Corporate Films", "Events", "Weddings", "Product Photography", "Social Reels", "Commercial Photography", "Real Estate Video", "Portrait Photography"];
  const filteredPosts = activeCategory === "All Work" ? posts : posts.filter(post => post.customFields?.category === activeCategory);
  if (loading) {
    return <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
        {[...Array(6)].map((_, i) => <div key={i} data-index={i} className="aspect-[4/3] rounded-lg bg-muted/30 animate-pulse" />)}
      </div>;
  }
  if (error) {
    return <div className="text-center py-12 bg-muted/10 rounded-lg border border-dashed border-border">
        <p className="text-muted-foreground font-sans">
          Unable to load our featured work at this time. Please try again later.
        </p>
      </div>;
  }
  return <div className="space-y-10">
      <Tabs value={activeCategory} onValueChange={setActiveCategory} className="w-full">
        <TabsList className="w-full justify-start overflow-x-auto bg-transparent border-b border-border p-0 h-auto flex-nowrap">
          {categories.map((cat, i) => <TabsTrigger key={cat} data-index={i} value={cat} className="relative px-5 py-4 text-sm md:text-base font-display font-medium border-b-2 border-transparent data-[state=active]:border-accent data-[state=active]:bg-transparent data-[state=active]:text-foreground data-[state=active]:shadow-none hover:text-accent transition-all duration-300 shrink-0">
              {cat}
            </TabsTrigger>)}
        </TabsList>

        <TabsContent value={activeCategory} className="mt-10 outline-none">
          {filteredPosts.length === 0 ? <div className="text-center py-20">
              <p className="text-muted-foreground font-sans text-lg">
                No projects found in "{activeCategory}".
              </p>
            </div> : <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
              {filteredPosts.slice(0, 9).map((post, i) => {
            const customFields = post.customFields || {};
            const projectTitle = customFields.project_title || "";
            const clientName = customFields.client_name || "";
            const categoryName = customFields.category || "";
            const coverImageUrl = customFields.cover_image || "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1529178/img/portfolio_placeholder.png";
            const videoUrl = customFields.video_url || "";
            const targetLink = post.link || `/PortfolioPage?id=${post.id}`;
            const coverImage = {
              src: coverImageUrl,
              srcset: "",
              sizes: ""
            };
            return <Link key={post.id} data-index={i} to={targetLink} className="group relative aspect-[4/3] overflow-hidden rounded-lg bg-primary-dark block shadow-medium hover:shadow-strong transition-all duration-500">
                    <img src={coverImage.src} data-wvc-srcset={coverImage.srcset} data-wvc-sizes={coverImage.sizes} alt={projectTitle || "Portfolio Project"} loading="lazy" className="size-full object-cover transition-transform duration-700 ease-out-back group-hover:scale-110" />

                    {/* Dark cinematic gradient overlay */}
                    <div className="absolute inset-0 bg-linear-to-t from-primary-dark/90 via-primary-dark/20 to-transparent opacity-60 group-hover:opacity-90 transition-opacity duration-500" />

                    {/* Centered play button overlay for video items */}
                    {Boolean(videoUrl) && <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                        <div className="size-12 rounded-full bg-accent/90 text-foreground-accent flex items-center justify-center shadow-lg transform transition-transform duration-300 group-hover:scale-110">
                          <Play className="size-5 fill-current ml-0.5" aria-hidden="true" />
                        </div>
                      </div>}

                    <div className="absolute inset-0 flex flex-col justify-end p-8 translate-y-4 group-hover:translate-y-0 transition-transform duration-500">
                      <div className="space-y-2">
                        {categoryName && <span className="inline-block px-3 py-1 bg-accent text-foreground-accent text-xs font-display font-bold tracking-wider uppercase rounded-sm">
                            {categoryName}
                          </span>}
                        {projectTitle && <h3 className="text-foreground-dark text-2xl font-display font-bold leading-tight">
                            {projectTitle}
                          </h3>}
                        {clientName && <p className="text-foreground-dark/70 text-sm font-sans line-clamp-1">
                            {clientName}
                          </p>}
                      </div>
                    </div>
                  </Link>;
          })}
            </div>}
        </TabsContent>
      </Tabs>
    </div>;
}
export default function FeaturedWork() {
  return <section id="featured-work" className="py-20 px-6 md:py-32 bg-background">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-16">
          <div className="max-w-2xl">
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-display font-bold text-foreground mb-6 leading-tight">
              Cinematic <span className="text-accent">Portfolios</span>
            </h2>
            <p className="text-muted-foreground text-lg md:text-xl font-sans leading-relaxed">
              A curated selection of our most impactful visual storytelling. From high-octane event coverage to intimate brand portraits.
            </p>
          </div>
          <div className="hidden md:block">
            <Link to="/PortfolioPage" className="inline-flex items-center gap-2 text-accent font-display font-bold hover:text-accent-hover transition-colors group">
              View All Projects
              <span className="w-8 h-px bg-accent group-hover:w-12 transition-all duration-300" />
            </Link>
          </div>
        </div>

        <PostsProvider wp_query={{
        post_type: "portfolio",
        per_page: 12,
        order: "desc",
        orderby: "date",
        status: ["publish"]
      }} className="featured-work-container">
          <ProjectsGridConsumer />
        </PostsProvider>

        <div className="mt-16 text-center md:hidden">
          <Link to="/PortfolioPage" className="inline-flex items-center justify-center w-full py-4 border border-primary text-primary font-display font-bold rounded-lg hover:bg-primary hover:text-foreground-primary transition-all">
            View All Projects
          </Link>
        </div>
      </div>
    </section>;
}